import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'axios';
import ENV_CONFIG from '../config/environment-unified';
import useTranslation from '../hooks/useTranslation';

const PageContainer = styled.div`
  max-width: 600px;
  margin: 0 auto;
  padding: 60px 20px;
  text-align: center;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const SuccessIcon = styled.div`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 30px;
  animation: scaleIn 0.5s ease-out;
  
  svg {
    width: 60px;
    height: 60px;
    color: white;
  }
  
  @keyframes scaleIn {
    from {
      transform: scale(0);
    }
    to {
      transform: scale(1);
    }
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: bold;
  color: ${({ theme }) => theme.color.primaryText};
  margin-bottom: 15px;
`;

const Message = styled.p`
  font-size: 18px;
  color: ${({ theme }) => theme.color.secondaryText};
  margin-bottom: 30px;
  line-height: 1.6;
`;

const InfoCard = styled.div`
  background: ${({ theme }) => theme.color.cardBackground};
  border-radius: 16px;
  padding: 30px;
  margin: 30px 0;
  border: 1px solid rgba(34, 197, 94, 0.3);
  box-shadow: 0 4px 20px rgba(34, 197, 94, 0.1);
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px 0;
  border-bottom: 1px solid rgba(255,255,255,0.1);
  
  &:last-child {
    border-bottom: none;
  }
`;

const InfoLabel = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.color.secondaryText};
`;

const InfoValue = styled.span`
  font-size: 16px;
  font-weight: bold;
  color: ${({ theme }) => theme.color.primaryText};
`;

const GCoinsDisplay = styled.div`
  font-size: 48px;
  font-weight: bold;
  color: #22c55e;
  margin: 20px 0;
  
  span {
    font-size: 20px;
    opacity: 0.8;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 30px;
  flex-wrap: wrap;
  justify-content: center;
`;

const Button = styled.button`
  padding: 12px 30px;
  font-size: 16px;
  font-weight: bold;
  color: ${({ secondary }) => secondary ? ({ theme }) => theme.color.primaryText : '#fff'};
  background: ${({ secondary }) =>
    secondary ? 'transparent' : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'};
  border: ${({ secondary }) => secondary ? '2px solid #667eea' : 'none'};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
  }
`;

const LoadingSpinner = styled.div`
  width: 50px;
  height: 50px;
  border: 4px solid rgba(34, 197, 94, 0.2);
  border-top-color: #22c55e;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const PaymentSuccess = () => {
  const history = useHistory();
  const location = useLocation();
  const { t, currentLanguage } = useTranslation();
  const [transaction, setTransaction] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [postPurchaseRedirect, setPostPurchaseRedirect] = useState(null);

  const getLocale = () => (currentLanguage === 'en' ? 'en-GB' : 'fr-FR');

  useEffect(() => {
    verifyPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromQuery = params.get('redirect');
    let fromStorage = null;
    try {
      fromStorage = localStorage.getItem('postGcoinPurchaseRedirect');
    } catch (_) {
      fromStorage = null;
    }

    const candidate = fromQuery || fromStorage;
    if (candidate && typeof candidate === 'string' && candidate.startsWith('/')) {
      setPostPurchaseRedirect(candidate);
    } else {
      setPostPurchaseRedirect(null);
    }
  }, [location.search]);

  const handleReturnAfterPurchase = () => {
    if (!postPurchaseRedirect) return;
    try {
      localStorage.removeItem('postGcoinPurchaseRedirect');
    } catch (_) {
      // ignore
    }
    history.push(postPurchaseRedirect);
  };

  const verifyPayment = async () => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');

    if (!sessionId) {
      setError(t('payments.missingSessionId', 'Session ID manquant'));
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(
        `${ENV_CONFIG.WALLET_BALANCE_ENDPOINT.replace('/balance', `/verify-payment/${sessionId}`)}`
      );

      if (response.data.success) {
        setTransaction(response.data.transaction);
      } else {
        setError(t('payments.processing', 'Le paiement est en cours de traitement...'));
        // Retry after 2 seconds
        setTimeout(verifyPayment, 2000);
      }
    } catch (err) {
      console.error('Error verifying payment:', err);
      setError(t('payments.errorVerifying', 'Erreur lors de la vérification du paiement'));
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingSpinner />
        <Message style={{ marginTop: '20px' }}>
          {t('payments.verifyingPayment', 'Vérification du paiement...')}
        </Message>
      </PageContainer>
    );
  }

  if (error) {
    return (
      <PageContainer>
        <SuccessIcon style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </SuccessIcon>
        <Title>{t('payments.verificationInProgressTitle', 'Vérification en cours...')}</Title>
        <Message>{error}</Message>
        <ButtonGroup>
          <Button onClick={() => window.location.reload()}>
            {t('payments.refresh', 'Rafraîchir')}
          </Button>
          <Button secondary onClick={() => history.push('/')}>
            {t('payments.backHome', "Retour à l'accueil")}
          </Button>
        </ButtonGroup>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <SuccessIcon>
        <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
      </SuccessIcon>

      <Title>🎉 {t('payments.successTitle', 'Paiement réussi !')}</Title>
      <Message>
        {t(
          'payments.successMessage',
          'Votre achat a été traité avec succès. Les G-Coins ont été ajoutés à votre wallet.'
        )}
      </Message>

      {transaction && (
        <InfoCard>
          <GCoinsDisplay>
            +{transaction.total_gcoins.toLocaleString()}
            <span> G-Coins</span>
          </GCoinsDisplay>

          <InfoRow>
            <InfoLabel>{t('payments.amountPaid', 'Montant payé')}</InfoLabel>
            <InfoValue>{transaction.amount_paid.toFixed(2)} {transaction.currency}</InfoValue>
          </InfoRow>

          <InfoRow>
            <InfoLabel>{t('payments.packPurchased', 'Pack acheté')}</InfoLabel>
            <InfoValue>{transaction.plan_name || 'G-Coins Pack'}</InfoValue>
          </InfoRow>

          <InfoRow>
            <InfoLabel>{t('payments.date', 'Date')}</InfoLabel>
            <InfoValue>
              {new Date(transaction.completed_at || transaction.created_at).toLocaleString(getLocale())}
            </InfoValue>
          </InfoRow>

          <InfoRow>
            <InfoLabel>{t('payments.transactionId', 'Transaction ID')}</InfoLabel>
            <InfoValue style={{ fontSize: '12px', fontFamily: 'monospace' }}>
              {transaction.id}
            </InfoValue>
          </InfoRow>
        </InfoCard>
      )}

      <Message style={{ fontSize: '14px', marginTop: '20px' }}>
        💌 {t('payments.confirmationEmailSent', 'Un email de confirmation a été envoyé à votre adresse email.')}
      </Message>

      <ButtonGroup>
        <Button onClick={() => history.push('/gcoin-transactions')}>
          {t('payments.viewWallet', 'Voir mon wallet')}
        </Button>
        {postPurchaseRedirect && (
          <Button onClick={handleReturnAfterPurchase}>
            {t('payments.returnToEvent', 'Retourner à l’événement')}
          </Button>
        )}
        <Button secondary onClick={() => history.push('/')}>
          {t('payments.backHome', "Retour à l'accueil")}
        </Button>
      </ButtonGroup>
    </PageContainer>
  );
};

export default PaymentSuccess;



